import { SignInButton, SignUpButton } from "@clerk/clerk-react";
import { CaretRight } from "@phosphor-icons/react";
import classNames from "classnames";
import * as React from "react";
import styles from "./Navbar.module.css";
import { NavItem } from "./NavItem";
import { Button } from "../Button";
import { AgentsDropdownContent } from "./dropdown-content/AgentsDropdownContent";
import { ResourcesDropdownContent } from "./dropdown-content/ResourcesDropdownContent";
import { SolutionsDropdownContent } from "./dropdown-content/SolutionsDropdownContent";
import { HamburgerIcon } from "./HamburgerIcon";
import { MobileMenu } from "./MobileMenu";
import { ProfileDropdown } from "./ProfileDropdown";
import { AuthenticationContext } from "~/contexts/authenticationContext";
import { useNavbarVisibility } from "~/contexts/navbarVisibilityContext";
import * as Image from "~/image";
import * as paths from "~/paths";
import { useClerkLoaded } from "~/utils/clerkDetection";
import {
  handleKeyboardClick,
  createDropdownKeyHandler,
} from "~/utils/keyboardUtils";

const ClerkAuthButtons: React.FC = () => {
  return (
    <>
      <SignInButton mode="modal">
        <span
          role="button"
          tabIndex={0}
          className={styles.signInLink}
          onKeyDown={handleKeyboardClick}
        >
          Sign in
        </span>
      </SignInButton>
      <SignUpButton mode="modal">
        <Button
          as="span"
          variant="secondary"
          size="small"
          role="button"
          tabIndex={0}
          onKeyDown={handleKeyboardClick}
        >
          Try for Free
        </Button>
      </SignUpButton>
    </>
  );
};

interface FallbackAuthButtonsProps {
  nimbusUrl?: string;
}

const FallbackAuthButtons: React.FC<FallbackAuthButtonsProps> = ({
  nimbusUrl,
}) => {
  return (
    <>
      <a href={paths.login({})} className={styles.signInLink}>
        Sign in
      </a>
      <Button
        as="a"
        href={nimbusUrl || "/products/negotiating-agent"}
        variant="secondary"
        size="small"
      >
        Try for Free
      </Button>
    </>
  );
};

interface SafeAuthButtonsProps {
  nimbusUrl?: string;
}

const SafeAuthButtons: React.FC<SafeAuthButtonsProps> = ({ nimbusUrl }) => {
  const hasClerk = useClerkLoaded();

  if (hasClerk === null) {
    return null;
  }
  if (!hasClerk) {
    return <FallbackAuthButtons nimbusUrl={nimbusUrl} />;
  }

  return <ClerkAuthButtons />;
};

export interface NavbarProps {
  className?: string;
  nimbusUrl?: string;
  chromeExtensionUrl?: string;
}

const VendrLogo = () => {
  const frameIdRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    return () => {
      if (frameIdRef.current !== null) {
        cancelAnimationFrame(frameIdRef.current);
      }
    };
  }, []);

  const handleClick = (e: React.MouseEvent) => {
    if (window.location.pathname === "/") {
      e.preventDefault();

      if (frameIdRef.current !== null) {
        cancelAnimationFrame(frameIdRef.current);
      }

      const startPosition = window.scrollY;
      const duration = 800;
      const startTime = performance.now();

      const easeInOutCubic = (t: number): number => {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      };

      const animateScroll = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const ease = easeInOutCubic(progress);

        window.scrollTo(0, startPosition * (1 - ease));

        if (progress < 1) {
          frameIdRef.current = requestAnimationFrame(animateScroll);
        } else {
          frameIdRef.current = null;
        }
      };

      frameIdRef.current = requestAnimationFrame(animateScroll);
    }
  };

  return (
    <a href="/" className={styles.logo} onClick={handleClick}>
      <img
        src={Image.vendrLogoWithPurpleText}
        alt="Vendr"
        width={94}
        height={24}
      />
    </a>
  );
};

const DROPDOWN_ITEMS = ["Agents", "Solutions", "Resources"] as const;

export const Navbar: React.FC<NavbarProps> = ({
  className,
  nimbusUrl,
  chromeExtensionUrl,
}) => {
  const { identity } = React.useContext(AuthenticationContext);
  const { isHidden, setIsHidden } = useNavbarVisibility();
  const isNavbarVisible = !isHidden;

  const [hoveredItem, setHoveredItem] = React.useState<string | null>(null);
  const [activeDropdown, setActiveDropdown] = React.useState<string | null>(
    null,
  );
  const [isExiting, setIsExiting] = React.useState(false);
  const [isPinned, setIsPinned] = React.useState(false);
  const [animationState, setAnimationState] = React.useState<
    "entering" | "stable" | "exiting"
  >("stable");
  const [contentKey, setContentKey] = React.useState(0);
  const [dropdownHeight, setDropdownHeight] = React.useState<number | null>(
    null,
  );
  const [isInitialMount, setIsInitialMount] = React.useState(false);
  const exitTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const lastScrollYRef = React.useRef(0);
  const navbarRef = React.useRef<HTMLElement | null>(null);
  const dropdownRef = React.useRef<HTMLDivElement | null>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const originalOverflowRef = React.useRef<string | null>(null);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [expandedMobileItem, setExpandedMobileItem] = React.useState<
    string | null
  >(null);
  const [mobileMenuAnimationState, setMobileMenuAnimationState] =
    React.useState<"closed" | "opening" | "open" | "closing">("closed");

  const isDropdownItem = (itemName: string) => {
    return DROPDOWN_ITEMS.includes(itemName as (typeof DROPDOWN_ITEMS)[number]);
  };

  const closeDropdown = React.useCallback(() => {
    if (activeDropdown && animationState !== "exiting") {
      setAnimationState("exiting");
      setIsExiting(true);
      setIsPinned(false);
      exitTimeoutRef.current = setTimeout(() => {
        setActiveDropdown(null);
        setAnimationState("stable");
        setIsExiting(false);
        setHoveredItem(null);
      }, 350);
    }
  }, [activeDropdown, animationState]);

  const handleNavItemMouseEnter = (itemName: string) => {
    setHoveredItem(itemName);

    if (isPinned && activeDropdown && activeDropdown !== itemName) {
      setIsPinned(false);
    }

    if (exitTimeoutRef.current) {
      clearTimeout(exitTimeoutRef.current);
    }

    if (isDropdownItem(itemName)) {
      if (activeDropdown && activeDropdown !== itemName) {
        setAnimationState("stable");
        setContentKey((k) => k + 1);
        setActiveDropdown(itemName);
        setIsExiting(false);
      } else if (!activeDropdown || animationState === "exiting") {
        setAnimationState("entering");
        setActiveDropdown(itemName);
        setIsExiting(false);
      }
    } else {
      if (!isPinned) {
        closeDropdown();
      }
    }
  };

  const handleDropdownMouseEnter = () => {
    if (exitTimeoutRef.current) {
      clearTimeout(exitTimeoutRef.current);
    }
    if (animationState === "exiting") {
      setAnimationState("stable");
      setIsExiting(false);
    }
  };

  const handleNavbarMouseLeave = () => {
    if (!isPinned) {
      setHoveredItem(null);
      closeDropdown();
    }
  };

  const handleNavItemClick = (itemName: string) => {
    if (!isDropdownItem(itemName)) {
      return;
    }

    if (isPinned && activeDropdown === itemName) {
      closeDropdown();
      return;
    }

    if (exitTimeoutRef.current) {
      clearTimeout(exitTimeoutRef.current);
    }

    setIsPinned(true);
    setHoveredItem(itemName);

    if (activeDropdown && activeDropdown !== itemName) {
      setAnimationState("stable");
      setContentKey((k) => k + 1);
      setActiveDropdown(itemName);
      setIsExiting(false);
    } else if (!activeDropdown || animationState === "exiting") {
      setAnimationState("entering");
      setActiveDropdown(itemName);
      setIsExiting(false);
    }
  };

  const toggleMobileMenu = () => {
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
      setMobileMenuAnimationState("closing");

      setTimeout(() => {
        setMobileMenuAnimationState("closed");
      }, 350);
    } else {
      setIsMobileMenuOpen(true);
      setMobileMenuAnimationState("open");

      if (!expandedMobileItem) {
        setExpandedMobileItem("Agents");
      }

      if (activeDropdown) {
        closeDropdown();
      }
    }
  };

  const toggleMobileItem = (itemName: string) => {
    if (expandedMobileItem === itemName) {
      setExpandedMobileItem(null);
    } else {
      setExpandedMobileItem(itemName);
    }
  };

  React.useEffect(() => {
    return () => {
      if (exitTimeoutRef.current) {
        clearTimeout(exitTimeoutRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (contentRef.current && activeDropdown) {
      const height = contentRef.current.scrollHeight;
      setDropdownHeight(height);
    } else {
      setDropdownHeight(null);
    }
  }, [activeDropdown, contentKey, animationState]);

  React.useEffect(() => {
    if (animationState === "entering") {
      setIsInitialMount(true);
      const frame = requestAnimationFrame(() => {
        setIsInitialMount(false);
      });
      return () => cancelAnimationFrame(frame);
    }
  }, [animationState]);

  React.useEffect(() => {
    if (!isPinned || !activeDropdown) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        navbarRef.current &&
        !navbarRef.current.contains(event.target as Node)
      ) {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [isPinned, activeDropdown, closeDropdown]);

  React.useEffect(() => {
    if (!isPinned || !activeDropdown) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeDropdown();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isPinned, activeDropdown, closeDropdown]);

  React.useEffect(() => {
    const SCROLL_THRESHOLD = 40;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDifference = lastScrollYRef.current - currentScrollY;
      const isAtTop = currentScrollY < 10;

      if (activeDropdown) {
        closeDropdown();
      }

      if (isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
        setMobileMenuAnimationState("closed");
      }

      if (isAtTop) {
        setIsHidden(false);
      } else if (scrollDifference > SCROLL_THRESHOLD) {
        setIsHidden(false);
      } else if (scrollDifference < -SCROLL_THRESHOLD) {
        setIsHidden(true);
      }

      lastScrollYRef.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeDropdown, isMobileMenuOpen, closeDropdown, setIsHidden]);

  React.useEffect(() => {
    if (isMobileMenuOpen) {
      if (originalOverflowRef.current === null) {
        originalOverflowRef.current = document.body.style.overflow;
      }
      document.body.style.overflow = "hidden";
    } else {
      if (originalOverflowRef.current !== null) {
        document.body.style.overflow = originalOverflowRef.current;
        originalOverflowRef.current = null;
      }
    }

    return () => {
      if (originalOverflowRef.current !== null) {
        document.body.style.overflow = originalOverflowRef.current;
        originalOverflowRef.current = null;
      }
    };
  }, [isMobileMenuOpen]);

  React.useEffect(() => {
    const handleResize = () => {
      if (
        window.innerWidth > 1025 &&
        (isMobileMenuOpen || mobileMenuAnimationState !== "closed")
      ) {
        setIsMobileMenuOpen(false);
        setMobileMenuAnimationState("closed");
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobileMenuOpen, mobileMenuAnimationState]);

  return (
    <>
      {activeDropdown && (
        <div
          className={`${styles.overlay} ${isExiting ? styles.overlayExit : ""}`}
        />
      )}
      <div
        className={`${styles.navbarContainer} ${!isNavbarVisible ? styles.navbarHidden : ""}`}
        data-navbar-hidden={!isNavbarVisible}
      >
        <a
          href={nimbusUrl || "/products/negotiating-agent"}
          className={styles.banner}
          aria-label="Meet Ruth, Vendr's AI negotiation agent"
        >
          <div className={styles.bannerGlow} />
          <div className={styles.bannerContent}>
            <span className={styles.bannerBadge}>New</span>
            <span className={styles.bannerText}>
              Meet Ruth, Vendr&apos;s AI negotiator
            </span>
            <span className={styles.bannerChevron}>
              <CaretRight
                className={styles.bannerChevronOut}
                size={14}
                weight="bold"
              />
              <CaretRight
                className={styles.bannerChevronIn}
                size={14}
                weight="bold"
              />
            </span>
          </div>
        </a>
        <nav
          ref={navbarRef}
          data-has-active={
            hoveredItem ||
            (isPinned && activeDropdown && !isExiting) ||
            undefined
          }
          className={classNames(styles.navbar, className)}
          onMouseLeave={handleNavbarMouseLeave}
        >
          <div className={styles.navbarInner}>
            <VendrLogo />

            <div className={styles.navItems}>
              <div
                role="button"
                tabIndex={0}
                onMouseEnter={() => handleNavItemMouseEnter("Agents")}
                onClick={() => handleNavItemClick("Agents")}
                onKeyDown={createDropdownKeyHandler(
                  "Agents",
                  handleNavItemClick,
                )}
                data-dropdown-active={activeDropdown === "Agents" && !isExiting}
              >
                <NavItem
                  label="Agents"
                  hasDropdown
                  isDropdownOpen={activeDropdown === "Agents" && !isExiting}
                />
              </div>
              <div
                role="button"
                tabIndex={0}
                onMouseEnter={() => handleNavItemMouseEnter("Solutions")}
                onClick={() => handleNavItemClick("Solutions")}
                onKeyDown={createDropdownKeyHandler(
                  "Solutions",
                  handleNavItemClick,
                )}
                data-dropdown-active={
                  activeDropdown === "Solutions" && !isExiting
                }
              >
                <NavItem
                  label="Solutions"
                  hasDropdown
                  isDropdownOpen={activeDropdown === "Solutions" && !isExiting}
                />
              </div>
              <div
                role="button"
                tabIndex={0}
                onMouseEnter={() => handleNavItemMouseEnter("Resources")}
                onClick={() => handleNavItemClick("Resources")}
                onKeyDown={createDropdownKeyHandler(
                  "Resources",
                  handleNavItemClick,
                )}
                data-dropdown-active={
                  activeDropdown === "Resources" && !isExiting
                }
              >
                <NavItem
                  label="Resources"
                  hasDropdown
                  isDropdownOpen={activeDropdown === "Resources" && !isExiting}
                />
              </div>
              <div onMouseEnter={() => handleNavItemMouseEnter("Pricing")}>
                <NavItem label="Pricing" href="/pricing" />
              </div>
            </div>

            <div className={styles.navActions}>
              {identity ? (
                <ProfileDropdown nimbusUrl={nimbusUrl} />
              ) : (
                <SafeAuthButtons nimbusUrl={nimbusUrl} />
              )}
            </div>

            <HamburgerIcon
              isOpen={isMobileMenuOpen}
              onClick={toggleMobileMenu}
            />
          </div>

          {activeDropdown && (
            <div
              ref={dropdownRef}
              className={`${styles.dropdownWrapper} ${
                animationState === "entering"
                  ? styles.dropdownWrapperEnter
                  : animationState === "exiting"
                    ? styles.dropdownWrapperExit
                    : styles.dropdownWrapperStable
              }`}
              style={{
                height:
                  animationState === "entering" && isInitialMount
                    ? "0px"
                    : animationState === "entering"
                      ? dropdownHeight !== null
                        ? `${dropdownHeight}px`
                        : "0px"
                      : animationState === "exiting"
                        ? "0px"
                        : dropdownHeight !== null
                          ? `${dropdownHeight}px`
                          : "auto",
                opacity:
                  animationState === "entering" && isInitialMount
                    ? 0
                    : undefined,
                transform:
                  animationState === "entering" && isInitialMount
                    ? "translateY(-20px) scaleY(0.92)"
                    : undefined,
              }}
              onMouseEnter={handleDropdownMouseEnter}
            >
              <div ref={contentRef}>
                {activeDropdown === "Agents" && (
                  <AgentsDropdownContent
                    key={`agents-${contentKey}`}
                    isSwitching={animationState === "stable"}
                    isExiting={isExiting}
                  />
                )}
                {activeDropdown === "Solutions" && (
                  <SolutionsDropdownContent
                    key={`solutions-${contentKey}`}
                    isSwitching={animationState === "stable"}
                    isExiting={isExiting}
                  />
                )}
                {activeDropdown === "Resources" && (
                  <ResourcesDropdownContent
                    key={`resources-${contentKey}`}
                    isSwitching={animationState === "stable"}
                    isExiting={isExiting}
                    chromeExtensionUrl={chromeExtensionUrl}
                  />
                )}
              </div>
            </div>
          )}

          <MobileMenu
            isOpen={isMobileMenuOpen}
            animationState={mobileMenuAnimationState}
            expandedItem={expandedMobileItem}
            onToggleItem={toggleMobileItem}
            nimbusUrl={nimbusUrl}
            chromeExtensionUrl={chromeExtensionUrl}
          />
        </nav>
      </div>
    </>
  );
};
